import { useCallback, useEffect, useState } from 'react'
import { Button, Chip } from '@heroui/react'
import { api, ApiError, type OrgLanguagesView } from '../lib/api'
import { moveLanguage, nativeIfOther, withoutLanguage } from '../lib/i18n/settingsEdit'
import type { LanguageCode, LanguageSettings } from '../lib/i18n/languages'
import { explain } from '../lib/staffErrors'
import { Icon } from '../ui/icons'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { useFlash } from './sectionKit'
import { ChoiceSelect, Notice, PageHead, SettingBlock, SettingsCard } from './settingsKit'
import '../styles/orgset.css'

/** Languages (#languages): the ordered customer languages, saved on each change. */
export function Languages({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const session = useSession()
  const { flash, toast } = useFlash()
  const [view, setView] = useState<OrgLanguagesView | null>(null)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState('')

  const load = useCallback(async () => {
    try {
      setView(await api.orgLanguages(organisation))
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setHidden(true)
      else setError(explain(err, lang, 'settings.languages.notLoaded'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation])

  useEffect(() => {
    setView(null)
    setHidden(false)
    setError('')
    void load()
  }, [load])

  const save = async (next: LanguageSettings) => {
    setBusy(true)
    setError('')
    try {
      setView(await api.saveOrgLanguages(next, organisation))
      void session?.refresh()
      flash(say('settings.languages.saved'))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  if (hidden) return <PageHead title={say('settings.languages')} />
  const name = (code: LanguageCode) => view?.known.find((l) => l.code === code)?.name ?? code
  const known = view?.known ?? []
  const rest = known.filter((l) => !view?.customer.includes(l.code))

  return (
    <>
      <PageHead title={say('settings.languages')} line={say('settings.languages.line')} />
      {toast}
      {error && <Notice className="os-gap">{error}</Notice>}
      {view && (
        <>
          <SettingsCard title={say('settings.languages.customers')} line={say('settings.languages.customersLine')}>
            <SettingBlock>
              <ul className="os-items__list os-langs">
                {view.customer.map((code, i) => (
                  <li key={code} className="os-items__row os-langs__row">
                    <span className="os-langs__name">
                      {name(code)}
                      {nativeIfOther(known, code) && (
                        <span className="os-langs__native" lang={code}>
                          {nativeIfOther(known, code)}
                        </span>
                      )}
                      {i === 0 && (
                        <Chip size="sm" variant="soft" color="accent">
                          {say('settings.languages.default')}
                        </Chip>
                      )}
                    </span>
                    <span className="os-items__acts">
                      <Button
                        isIconOnly
                        variant="ghost"
                        size="sm"
                        isDisabled={busy || i === 0}
                        aria-label={say('settings.languages.up', { name: name(code) })}
                        onPress={() => void save({ customer: moveLanguage(view.customer, code, -1) })}
                      >
                        <Icon name="up" size={16} />
                      </Button>
                      <Button
                        isIconOnly
                        variant="ghost"
                        size="sm"
                        isDisabled={busy || i === view.customer.length - 1}
                        aria-label={say('settings.languages.down', { name: name(code) })}
                        onPress={() => void save({ customer: moveLanguage(view.customer, code, 1) })}
                      >
                        <Icon name="down" size={16} />
                      </Button>
                      <Button
                        isIconOnly
                        variant="ghost"
                        size="sm"
                        isDisabled={busy || view.customer.length === 1}
                        aria-label={say('settings.languages.remove', { name: name(code) })}
                        onPress={() => void save({ customer: withoutLanguage(view.customer, code) })}
                      >
                        <Icon name="close" size={16} />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
              {rest.length > 0 && (
                <form
                  className="os-items__add os-langs__add"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (!adding) return
                    void save({ customer: [...view.customer, adding] }).then(() => setAdding(''))
                  }}
                >
                  <ChoiceSelect
                    label={say('settings.languages.addPick')}
                    value={adding}
                    onChange={setAdding}
                    placeholder={say('settings.languages.addPick')}
                    options={rest.map((l) => ({ value: l.code, label: l.name }))}
                  />
                  <Button type="submit" variant="secondary" isDisabled={busy || !adding}>
                    <Icon name="plus" size={16} /> {say('settings.languages.add')}
                  </Button>
                </form>
              )}
            </SettingBlock>
          </SettingsCard>
        </>
      )}
    </>
  )
}
